import * as React from 'react';
import { FiTrendingUp } from 'react-icons/fi';
import Card from 'components/Card';
import SectionTitle from 'components/SectionTitle';
import abbreviateNumber from 'utils/shared/number/abbreviateNumber';
import BarChartTrends from 'screens/TickerResearchPage/BarChartTrends';
import BarChartExpiryTrends from 'screens/TickerResearchPage/BarChartExpiryTrends';
import BarChartStrikeTrends from 'screens/TickerResearchPage/BarChartStrikeTrends';
import { BarChartSectionProps, ValueType } from 'screens/TickerResearchPage/props';
import {
  buildTradesByDate,
  buildTradesByExpiry,
  buildTradesByStrike,
} from 'screens/TickerResearchPage/buildBarChartGroups';

enum GraphType {
  Trend,
  ExpiryTrend,
  StrikeTrend,
}

const GRAPH_FILTERS = [
  {
    name: 'Net Premium by Trade Date',
    label: 'Premium',
    prefix: '$',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.Trend,
    dataKey: 'premium' as const,
    valueType: ValueType.Net,
  },
  {
    name: 'Total Premium by Trade Date',
    label: 'Premium',
    prefix: '$',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.Trend,
    dataKey: 'premium' as const,
    valueType: ValueType.Total,
  },
  {
    name: 'Net Volume by Trade Date',
    label: 'Contracts',
    prefix: '',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.Trend,
    dataKey: 'volume' as const,
    valueType: ValueType.Net,
  },
  {
    name: 'Total Volume by Trade Date',
    label: 'Contracts',
    prefix: '',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.Trend,
    dataKey: 'volume' as const,
    valueType: ValueType.Total,
  },
  {
    name: 'Net Volume by Expiry',
    label: 'Contracts',
    prefix: '',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.ExpiryTrend,
    dataType: 'volume' as const,
    valueType: ValueType.Net,
  },
  {
    name: 'Net Premium by Expiry',
    label: 'Premium',
    prefix: '$',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.ExpiryTrend,
    dataType: 'premium' as const,
    valueType: ValueType.Net,
  },
  {
    name: 'Net Volume by Strike',
    label: 'Contracts',
    prefix: '',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.StrikeTrend,
    dataType: 'volume' as const,
    valueType: ValueType.Net,
  },
  {
    name: 'Net Premium by Strike',
    label: 'Premium',
    prefix: '$',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.StrikeTrend,
    dataType: 'premium' as const,
    valueType: ValueType.Net,
  },
  {
    name: 'Total Volume by Expiry',
    label: 'Contracts',
    prefix: '',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.ExpiryTrend,
    dataType: 'volume' as const,
    valueType: ValueType.Total,
  },
  {
    name: 'Total Premium by Expiry',
    label: 'Premium',
    prefix: '$',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.ExpiryTrend,
    dataType: 'premium' as const,
    valueType: ValueType.Total,
  },
  {
    name: 'Total Volume by Strike',
    label: 'Contracts',
    prefix: '',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.StrikeTrend,
    dataType: 'volume' as const,
    valueType: ValueType.Total,
  },
  {
    name: 'Total Premium by Strike',
    label: 'Premium',
    prefix: '$',
    formatter: (num: number) => abbreviateNumber(num),
    graphType: GraphType.StrikeTrend,
    dataType: 'premium' as const,
    valueType: ValueType.Total,
  },
];

const BarChartSectionGrid: React.FC<React.PropsWithChildren<BarChartSectionProps>> = ({
  activeTicker,
  trades,
}) => {
  const tradesByDate = React.useMemo(() => buildTradesByDate(trades), [trades]);
  const tradesByExpiry = React.useMemo(() => buildTradesByExpiry(trades), [trades]);
  const tradesByStrike = React.useMemo(() => buildTradesByStrike(trades), [trades]);

  return (
    <div className="h-full w-full">
      <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold md:text-xl">
        <FiTrendingUp className="text-brand-green-primary" />
        {activeTicker || 'Ticker'} Trends
      </h3>
      {trades.length > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
          {GRAPH_FILTERS.map((g) => (
            <Card key={g.name} className="flex h-[300px] flex-col md:h-[400px]" isDark>
              <SectionTitle Icon={FiTrendingUp} title={g.name} />
              <div className="w-full max-w-full flex-1 overflow-hidden">
                {g.graphType === GraphType.Trend && (
                  <BarChartTrends
                    tradesByDate={tradesByDate}
                    label={g.label}
                    prefix={g.prefix}
                    formatter={g.formatter}
                    dataType={g.dataKey}
                    valueType={g.valueType}
                  />
                )}
                {g.graphType === GraphType.ExpiryTrend && (
                  <BarChartExpiryTrends
                    tradesByExpiry={tradesByExpiry}
                    label={g.label}
                    prefix={g.prefix}
                    formatter={g.formatter}
                    dataType={g.dataType!}
                    valueType={g.valueType}
                  />
                )}
                {g.graphType === GraphType.StrikeTrend && (
                  <BarChartStrikeTrends
                    tradesByStrike={tradesByStrike}
                    label={g.label}
                    prefix={g.prefix}
                    formatter={g.formatter}
                    dataType={g.dataType!}
                    valueType={g.valueType}
                  />
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default BarChartSectionGrid;
